import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import FontIcon from './FontIcon';
import AppIcon from '../AppIcon';
import WebsiteDisplayItem from './WebsiteDisplayItem';
import HotSearchContent from '../popup/cards/HotSearchContent';
import DailyEnglishContent from '../popup/cards/daily-english/DailyEnglishContent';
import logger from '../../utils/logger';
import './SortableAppItem.css';
import trackingManager from '../../services/trackingManager';
import { DATA_TYPE } from '../../config/TypeConfig';
import languageManager from '../../utils/languageManager';

const SortableAppItem = ({
  app,
  onEdit,
  onDelete,
  activeContextMenu,
  setActiveContextMenu,
  isBatchEditing,
  setShowWallpaperPopup,
  iconSize = 64,
  iconRadius = 12,
  iconTextColor = '#ffffff',
  // 新增：上层组件传入的事件处理器
  onItemClick,
  onItemDragStart
}) => {
  // 获取显示模式状态
  const { websiteDisplayMode } = useSelector(state => state.settings);
  
  // 状态定义
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const contextMenuRef = useRef(null);
  
  // 当activeContextMenu改变时，关闭非活动菜单
  useEffect(() => {
    if (activeContextMenu !== app.complexId && activeContextMenu !== app.id) {
      setShowContextMenu(false);
    }
  }, [activeContextMenu, app.complexId, app.id]);
  
  // 添加点击外部区域关闭菜单的逻辑
  useEffect(() => {
    // 只在菜单显示时添加事件监听
    if (!showContextMenu) return;
    
    const handleClickOutside = (event) => {
      // 检查点击是否在菜单外部
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setShowContextMenu(false);
        setActiveContextMenu(null);
      }
    };
    
    // 添加全局点击事件监听器
    document.addEventListener('mousedown', handleClickOutside);
    
    // 组件卸载或菜单关闭时清理事件监听器
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showContextMenu, setActiveContextMenu]);

  // 处理点击事件（有了拖动手柄后，其他地方只需要正常处理点击）
  const handleClick = (e) => {
    // 如果点击的是拖动手柄、删除按钮或菜单项，不处理
    const shouldIgnoreClick = e.target.closest('.drag-handle, .batch-delete-button, .home-context-menu, .menu-item');
    if (shouldIgnoreClick) {
      return;
    }
    
    // 正常处理点击事件
    if (onItemClick) {
      onItemClick(app, e);
    }
  };
  
  // 处理右键菜单事件
  const handleContextMenu = (e) => {
    e.preventDefault();
    setActiveContextMenu(app.complexId || app.id);
    setContextMenuPosition({ 
      x: e.clientX,
      y: e.clientY
    });
    setShowContextMenu(true);
  };
  
  // 关闭上下文菜单
  const closeContextMenu = () => {
    setShowContextMenu(false);
    setActiveContextMenu(null);
  };

  // 元素样式设置
  const style = {
    zIndex: showContextMenu ? 1000 : 1,
  };
  
  // 图片加载失败时回退到文字图标
  const handleImageError = () => {
    logger.log('图标加载失败，应用ID:', app.id, '复合ID:', app.complexId);
    const iconErrorEvent = new CustomEvent('iconLoadError', {
      detail: {
        appId: app.id
      }
    });
    window.dispatchEvent(iconErrorEvent);
  };
  
  // 批量删除处理
  const handleBatchDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (onDelete) {
      onDelete(app.originalId || app.id);
    }
  };

  // 创建应用数据对象
  const createAppDataObject = () => {
    return {
      id: app.complexId || app.id,
      title: app.name,
      name: app.name,
      url: app.url,
      ico: app.ico || app.iconUrl,
      bgColor: app.bgColor,
      textIcon: app.textIcon,
      preferredIconType: app.preferredIconType,
      iconColor: app.iconColor,
      complexId: app.complexId
    };
  };
  
  // 处理打开链接
  const handleOpen = () => {
    // 触发网站点击事件，用于统计
    if (app.url) {
      const clickEvent = new CustomEvent('websiteClick', {
        detail: { website: app }
      });
      window.dispatchEvent(clickEvent);
    }
    if (app.url) {
      window.open(app.url, '_blank');
    }
    closeContextMenu();
  };

  // 处理编辑
  const handleEdit = () => {
    if (onEdit) {
      onEdit(app);
    }
    closeContextMenu();
  };

  // 处理删除
  const handleDelete = () => {
    if (onDelete) {
      onDelete(app.originalId || app.id);
    }
    closeContextMenu();
  };
  
  // 决定显示图片图标还是文字图标或默认图标
  const renderIcon = () => {
    return (
      <AppIcon 
        ico={app.ico}
        iconUrl={app.iconUrl}
        textIcon={app.textIcon}
        bgColor={app.bgColor}
        preferredIconType={app.preferredIconType}
        iconColor={app.iconColor || 'white'}
        onImageError={handleImageError}
        size={iconSize}
        wrapperStyle={{ 
          borderRadius: `${iconRadius}px`,
          width: `${iconSize}px`,
          height: `${iconSize}px`
        }}
      />
    );
  };
  
  // 渲染删除按钮
  const renderDeleteButton = () => {
    if (!isBatchEditing) return null;
    
    return (
      <div 
        className="batch-delete-button"
        onClick={handleBatchDelete}
        style={{
          position: 'absolute',
          top: '2px',
          right: '2px',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: 'red',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: 'pointer',
          zIndex: 9999,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
          pointerEvents: 'auto',
          border: '2px solid white'
        }}
        title="删除此项目"
      >
        ×
      </div>
    );
  };
  
  // 渲染拖动手柄
  const renderDragHandle = () => {
    // 计算拖动手柄的圆角，确保与图标完全匹配
    const handleRadius = iconRadius;
    
    return (
      <div 
        className="drag-handle"
        style={{
          borderTopRightRadius: `${handleRadius}px`
        }}
        title={languageManager.t('common.dragToMove')}
        onClick={(e) => {
          // 阻止点击事件冒泡，避免触发应用的点击事件
          e.stopPropagation();
        }}
      >
        <FontIcon 
          name="hand-paper" 
          style={{ 
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.85)',
            cursor: 'grab'
          }} 
        />
      </div>
    );
  };
  
  // 渲染上下文菜单
  const renderContextMenu = () => {
    if (!showContextMenu) return null;
    
    return ReactDOM.createPortal(
      <div
        ref={contextMenuRef}
        className="home-context-menu"
        style={{
          position: 'fixed',
          left: `${contextMenuPosition.x}px`,
          top: `${contextMenuPosition.y}px`,
          zIndex: 1001
        }}
      >
        <div 
          className="menu-item"
          onClick={(e) => {
            e.stopPropagation();
            handleOpen();
          }}
        >
          <FontIcon name="external-link-alt" style={{ width: '20px', marginRight: '8px' }} />
          <span>{languageManager.t('home.contextMenu.openNavigation')}</span>
        </div>
        <div 
          className="menu-item"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit();
          }}
        >
          <FontIcon name="edit" style={{ width: '20px', marginRight: '8px' }} />
          <span>{languageManager.t('home.contextMenu.editNavigation')}</span>
        </div>
        <div 
          className="menu-item"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
        >
          <FontIcon name="trash-alt" style={{ width: '20px', marginRight: '8px' }} />
          <span>{languageManager.t('home.contextMenu.deleteNavigation')}</span>
        </div>
        <div 
          className="menu-item"
          onClick={(e) => {
            e.stopPropagation();
            setShowWallpaperPopup(true);
            closeContextMenu();
          }}
        >
          <FontIcon name="image" style={{ width: '20px', marginRight: '8px' }} />
          <span>{languageManager.t('home.contextMenu.changeWallpaper')}</span>
        </div>
      </div>,
      document.body
    );
  };
  
  // 渲染应用图标包装区域
  const renderAppIconWrapper = () => {
    const backgroundStyle = app.bgColor === 'transparent' 
      ? 'linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee), linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee)'
      : (app.bgColor || 'white');
      
    const backgroundSizeStyle = app.bgColor === 'transparent' ? '16px 16px' : undefined;
    const backgroundPositionStyle = app.bgColor === 'transparent' ? '0 0, 8px 8px' : undefined;
    const cursorStyle = app.url ? 'pointer' : 'default';
    
    const wrapperClassName = `app-icon-wrapper ${isBatchEditing ? 'batch-edit-mode' : ''} ${app.bgColor === 'transparent' ? 'transparent' : ''}`;
    
    return (
      <div 
        className={wrapperClassName}
        style={{ 
          background: backgroundStyle,
          backgroundSize: backgroundSizeStyle,
          backgroundPosition: backgroundPositionStyle,
          cursor: cursorStyle
        }}
        data-app-id={app.originalId || app.id}
        data-app-complex-id={app.complexId}
        data-app-name={app.name}
        data-app-url={app.url}
        data-app-ico={app.ico || app.iconUrl}
        data-app-bgcolor={app.bgColor}
        onContextMenu={handleContextMenu}
      >
        {renderIcon()}
      </div>
    );
  };
  
  // 根据 dataType 渲染不同的自定义卡片组件
  const renderCustomCard = () => {
    switch (app.dataType) {
      case DATA_TYPE.HOT_SEARCH:
        return (
          <HotSearchContent
            app={app}
            iconSize={iconSize}
            iconRadius={iconRadius}
            iconTextColor={iconTextColor}
          />
        );
      case DATA_TYPE.DAILY_ENGLISH:
        return (
          <DailyEnglishContent
            app={app}
            iconSize={iconSize}
            iconRadius={iconRadius}
            iconTextColor={iconTextColor}
          />
        );
      default:
        return null;
    }
  };

  // 如果有自定义卡片，优先渲染
  const customCard = renderCustomCard();
  if (customCard) {
    return (
      <div 
        style={{
          ...style,
          width: '100%',
          height: '100%',
          position: 'relative'
        }}
        // 为自定义卡片添加右键菜单支持
        onContextMenu={handleContextMenu}
        // 处理点击事件
        onClick={handleClick}
      >
        <div 
          style={{
            ...style,
            width: '100%',
            height: '100%',
            position: 'relative' // 确保相对定位
          }}
          className={`app-item custom-card ${isBatchEditing ? 'batch-editing' : ''}`}
          data-app-id={app.complexId || app.id}
        >
          {customCard}
        </div>
        {/* 删除按钮放在外层，避免被内层事件阻止 */}
        {renderDeleteButton()}
        {/* 拖动手柄 */}
        {renderDragHandle()}
        {renderContextMenu()}
      </div>
    );
  }

  // 默认渲染普通网站导航项目
  return (
    <div 
      style={{
        ...style,
        width: websiteDisplayMode === 'detailed' ? `${iconSize + 100}px` : `${iconSize + 15}px`,
        position: 'relative'
      }}
      className={`app-item ${isBatchEditing ? 'batch-editing' : ''}`}
      data-app-id={app.complexId || app.id}
      onContextMenu={handleContextMenu}
    >
      <WebsiteDisplayItem
        displayMode={websiteDisplayMode}
        app={app}
        iconSize={iconSize}
        iconRadius={iconRadius}
        iconTextColor={iconTextColor}
        onImageError={handleImageError}
        onContextMenu={handleContextMenu}
        // 处理点击事件
        onClick={handleClick}
      />
      
      {/* 删除按钮放在外层，确保可以正常点击 */}
      {renderDeleteButton()}
      {/* 拖动手柄 */}
      {renderDragHandle()}
      {renderContextMenu()}
    </div>
  );
};

export default SortableAppItem; 